import CheckBox from 'components/login/CheckBox';
import React, {useState} from 'react';
import styled from 'styled-components';
import {colors} from 'styles/colors';
import {useFormatMsgArray} from 'utils/useFormatMsg';
import CommonModal from './CommonModal';
import DefaultButton from './DefaultButton';
import DefaultText from './DefaultText';
import DefaultImageView from './DefaultImageView';
import {minmax} from 'utils/minmax';

/**
 * 약관 컴포넌트의 Props 타입
 * @property {{service: boolean; privacy: boolean; location: boolean}} value - 약관 동의 상태
 * @property {any} setValue - 약관 동의 상태 설정 함수
 * @property {boolean} [readOnly] - 읽기 전용 여부
 * @property {boolean} [error] - 에러 여부
 */
type Props = {
  value: {
    service: boolean;
    privacy: boolean;
    location: boolean;
  };
  setValue: any;
  readOnly?: boolean;
  error?: boolean;
};

/**
 * 모달 열림 상태 인터페이스
 * @property {'all' | 'service' | 'privacy' | 'location' | ''} mode - 모달 모드
 * @property {boolean} status - 모달 상태
 */
interface IModalOpen {
  mode: 'all' | 'service' | 'privacy' | 'location' | '';
  status: boolean;
}

/**
 * 약관 컴포넌트
 * 서비스 이용약관, 개인정보 처리방침, 위치정보 이용약관 동의를 관리하는 컴포넌트입니다.
 * @param {Props} props - 약관 컴포넌트의 props
 * @returns {JSX.Element} 약관 컴포넌트
 */
const Terms = ({value, setValue, readOnly, error}: Props) => {
  const textArray = [
    'allTermAgree',
    'allTermDetail',
    'termEssential',
    'termTitleService',
    'termTitlePrivacy',
    'termTitleLocation',
    'allTermAgreeButton',
    'term',
    'termDetailService',
    'termDetailPrivacy',
    'termDetailLocation',
    'agree',
  ];
  const text = useFormatMsgArray(textArray);

  const [modalOpen, setModalOpen] = useState<IModalOpen>({
    mode: '',
    status: false,
  });
  const [switchNum, setSwitchNum] = useState(0);

  const allCheck = value.service && value.privacy && value.location;

  const eachCheck = (title: string) => {
    const valuesKey = Object.keys(value).filter(key =>
      title.toLowerCase().includes(key),
    )[0] as 'service' | 'privacy' | 'location';
    // eslint-disable-next-line no-constant-condition
    return {valuesKey, valueStatus: value[valuesKey]};
  };

  const titleData = [
    'termTitleService',
    'termTitlePrivacy',
    'termTitleLocation',
  ];
  const detailData = [
    'termDetailService',
    'termDetailPrivacy',
    'termDetailLocation',
  ];

  return (
    <Container style={{border: error ? '1px solid red' : ''}}>
      <AllTerm>
        <CheckBox
          style={{
            alignItems: 'flex-start',
          }}
          checked={allCheck}
          onClickWrap={() => {
            // 전체 동의 false : 전체 설명 모달
            if (!allCheck) {
              !readOnly &&
                setModalOpen({
                  mode: 'all',
                  status: true,
                });
            } else {
              !readOnly &&
                setValue((prev: any) => ({
                  ...prev,
                  service: false,
                  privacy: false,
                  location: false,
                }));
            }
          }}
          checkOnImg="images/common/term_all_check_on.png"
          checkOffImg="images/common/term_all_check_off.png">
          <AllTexts>
            <strong>{text.allTermAgree}</strong>
            <p>{text.allTermDetail}</p>
          </AllTexts>
        </CheckBox>
      </AllTerm>

      <TermList>
        {titleData.map((title, index) => (
          <CheckBox
            key={title}
            checked={eachCheck(title).valueStatus}
            onChange={() => {
              // 현재 클릭한 약관 동의 true : 동의 false
              if (eachCheck(title).valueStatus) {
                !readOnly &&
                  setValue((prev: any) => ({
                    ...prev,
                    [eachCheck(title).valuesKey]: false,
                  }));
              }
            }}
            onClickWrap={() => {
              // 현재 클릭한 약관 동의 false : 설명 모달
              if (eachCheck(title).valueStatus) {
                !readOnly &&
                  setValue((prev: any) => ({
                    ...prev,
                    [eachCheck(title).valuesKey]: false,
                  }));
              } else {
                !readOnly && setSwitchNum(index);
                !readOnly &&
                  setModalOpen({
                    mode: eachCheck(title).valuesKey,
                    status: true,
                  });
              }
            }}
            checkOnImg="images/common/term_check_on.png"
            checkOffImg="images/common/term_check_off.png">
            <TermItem>
              <p>{text.termEssential + ' ' + text[title]}</p>
              <img src="images/common/term_arrow.png" alt="arrow" />
            </TermItem>
          </CheckBox>
        ))}
      </TermList>

      {modalOpen.status && (
        <CommonModal
          containerStyle={{
            padding: 'max(15px, min(1.6vw, 30px)) 0',
            maxHeight: 'fit-content',
          }}
          closeModal={() =>
            setModalOpen({
              mode: '',
              status: false,
            })
          }>
          <>
            <ModalWrap>
              <ModalHeader>
                <DefaultText
                  text="term"
                  textStyle={{
                    fontSize: 'max(18px, min(2vw, 36px))',
                    fontWeight: 500,
                  }}
                />
                <DefaultImageView
                  onClick={() =>
                    setModalOpen({
                      mode: '',
                      status: false,
                    })
                  }
                  source="images/common/close.png"
                  style={{
                    width: 'max(10px, min(1vw, 20px))',
                    cursor: 'pointer',
                  }}
                />
              </ModalHeader>

              <ModalContentsWrap>
                <DefaultText
                  text={titleData[switchNum]}
                  textStyle={{
                    fontSize: 'max(12px, min(1.2vw, 24px))',
                    fontWeight: 400,
                    marginBottom: 'max(8px, min(1.2vw, 16px))',
                  }}
                />
                <ModalTermDetailWrap>
                  <ModalTermDetail>
                    {text[detailData[switchNum]]}
                  </ModalTermDetail>
                </ModalTermDetailWrap>
              </ModalContentsWrap>

              <ModalAgreeButton
                type="button"
                onClick={() => {
                  if (modalOpen.mode === 'all') {
                    setValue((prev: any) => ({
                      ...prev,
                      service: true,
                      privacy: true,
                      location: true,
                    }));
                  } else {
                    setValue((prev: any) => ({
                      ...prev,
                      [modalOpen.mode]: true,
                    }));
                  }
                  setModalOpen({
                    mode: '',
                    status: false,
                  });
                }}>
                {modalOpen.mode === 'all'
                  ? text.allTermAgreeButton
                  : text.agree}
              </ModalAgreeButton>
            </ModalWrap>
            {modalOpen.mode === 'all' && (
              <ModalSwitchWrap>
                {switchNum !== 0 && (
                  <ModalSwitchButton
                    onClick={() =>
                      setSwitchNum((prev: number) =>
                        prev === 0 ? 0 : prev - 1,
                      )
                    }
                    src="images/common/term_switch_left.png"
                    style={{
                      position: 'absolute',
                      left: 0,
                    }}
                  />
                )}

                {switchNum !== 2 && (
                  <ModalSwitchButton
                    onClick={() =>
                      setSwitchNum((prev: number) =>
                        prev === 2 ? 2 : prev + 1,
                      )
                    }
                    src="images/common/term_switch_right.png"
                    style={{
                      position: 'absolute',
                      right: 0,
                    }}
                  />
                )}
              </ModalSwitchWrap>
            )}
          </>
        </CommonModal>
      )}
    </Container>
  );
};

export default Terms;

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  row-gap: max(12px, min(1.2vw, 24px));
  border-radius: ${minmax(10)};
`;

const AllTerm = styled.div`
  width: 100%;
  padding: max(12px, min(1.2vw, 24px));
  background-color: ${colors.lighterGray};
  border-radius: ${minmax(10)};
`;

const AllTexts = styled.div`
  width: 100%;
  margin-left: max(6px, min(0.6vw, 12px));
  display: flex;
  flex-direction: column;
  row-gap: 8px;
  strong {
    display: flex;
    align-items: center;
    height: max(15px, min(1.6vw, 30px));
    font-size: ${minmax(20)};
    font-weight: 400;
  }
  p {
    font-size: minmax(20);
    color: ${colors.darkGray};
    font-weight: 400;
  }
`;

const TermList = styled.ul`
  display: flex;
  flex-direction: column;
  row-gap: max(8px, min(1.2vw, 16px));
  width: 100%;
  padding: max(12px, min(1.2vw, 24px));
  border: 1px solid ${colors.lightGrey};
  border-radius: ${minmax(10)};
`;

const TermItem = styled.li`
  width: 100%;
  margin-left: max(6px, min(0.6vw, 12px));
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  p {
    font-size: ${minmax(20)};
  }
  img {
    width: max(15px, min(1.6vw, 30px));
    height: max(15px, min(1.6vw, 30px));
  }
`;

const ModalWrap = styled.div`
  width: 100%;
  height: fit-content;
  display: flex;
  flex-direction: column;
  padding: 0 max(20px, min(2vw, 40px));
`;

const ModalHeader = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const ModalContentsWrap = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: max(18px, min(2vw, 36px)) 0;
`;

const ModalTermDetailWrap = styled.div`
  width: 100%;
  flex: 1 0 auto;
  /* height: max(200px, min(18.7vw, 360px)); */
  height: 30vh;
  overflow-y: auto;

  // 스크롤바 삭제
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera*/
  }
`;

const ModalSwitchWrap = styled.div`
  width: 100%;
  position: absolute;
  display: flex;
  justify-content: space-between;
`;

const ModalSwitchButton = styled.img`
  width: max(40px, min(3vw, 60px));
  cursor: pointer;
`;

const ModalTermDetail = styled.pre`
  width: 100%;
  font-size: ${minmax(20)};
  fontweight: 300;
  line-height: 1.5;
  white-space: pre-wrap;
`;

const ModalAgreeButton = styled.button`
  width: 100%;
  height: max(40px, min(4.5vw, 80px));
  border-radius: ${minmax(10)};
  background-color: ${colors.primary2};
  color: ${colors.white};
  font-size: ${minmax(20)};
`;
